import React, { FC, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { getEntitiesById } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import EventCalendar from '../event_calendar/EventCalendar';
import moment from 'moment';
import { Views } from 'react-big-calendar';

const queryString = require('qs');

interface ReduxCalendarProps {
	location: any;
	history: any;
	match: any;
	collectionName: string;
	targetCollectionName: string;
	initialDateRange: any;
	updateFilters: any;
	data: any[];
	renderFunction: any[];
	additionalParams: any;
	updateQueryParams?: boolean;
	filters: any;
	sorting: any;
	emptyState?: any;
	transformRecords: any;
	initialFilters: any;
	EventComponent: any;
	fetching: boolean;
	onSelectEvent: any;
	fetchData: (
		params: any,

		targetCollectionName: string | null,
		sorting: { sort_by: string; order: string } | {},
		filters: any,
		addToTargetCollection: boolean
	) => any;
}

const ReduxCalendar: FC<ReduxCalendarProps> = ({
	updateFilters,
	history,
	location,
	updateQueryParams,
	targetCollectionName,
	filters,
	additionalParams = {},
	fetchData,
	sorting,
	data,
	initialDateRange,
	EventComponent,
	transformRecords,
	onSelectEvent,
}): React.ReactElement => {
	const getStartAndEndDate = (dateResponse) => {
		if (Array.isArray(dateResponse)) {
			return {
				startDate:
					dateResponse.length === 1
						? moment(dateResponse[0]).subtract(1, 'day').format('YYYY-MM-DD')
						: moment(dateResponse[0]).format('YYYY-MM-DD'),
				endDate:
					dateResponse.length === 1
						? moment(dateResponse[0]).add(1, 'day').format('YYYY-MM-DD')
						: moment(dateResponse[dateResponse.length - 1]).format('YYYY-MM-DD'),
			};
		} else if (typeof dateResponse == 'object' && dateResponse.start && dateResponse.end) {
			const startDate = moment(dateResponse.start).format('YYYY-MM-DD');
			const endDate = moment(dateResponse.end).format('YYYY-MM-DD');
			return { startDate, endDate };
		}

		return {};
	};

	const handleCalendarChange = useCallback(
		(dateResponse) => {
			const dateFilters = getStartAndEndDate(dateResponse);
			if (updateQueryParams) {
				const queryParams = queryString.stringify({
					...filters,
					...dateFilters,
					...additionalParams,
				});
				history.replace(`${location.pathname}?${queryParams}`);
			}
			updateFilters(getStartAndEndDate(dateResponse), targetCollectionName);
		},
		[
			additionalParams,
			filters,
			history,
			location.pathname,
			targetCollectionName,
			updateFilters,
			updateQueryParams,
		]
	);

	useEffect(() => {
		fetchData(additionalParams || {}, targetCollectionName, sorting, filters, false);
	}, [filters, sorting, targetCollectionName]);

	const calendarEvents = transformRecords(data);

	return (
		<EventCalendar
			events={calendarEvents}
			defaultDate={
				initialDateRange && Array.isArray(initialDateRange) && initialDateRange.length === 2
					? new Date((initialDateRange[0].getTime() + initialDateRange[1].getTime()) / 2.0)
					: undefined
			}
			defaultView={Views.WEEK}
			onRangeChange={handleCalendarChange}
			views={{
				week: true,
				day: true,
			}}
			components={{
				event: EventComponent,
			}}
			onSelectEvent={onSelectEvent}
		/>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { targetCollectionName, collectionName } = ownProps;
	const relevantState = state[collectionName];
	if (!relevantState) {
		throw new Error(
			'collection not found in root reducer. please check name for typos or add reducer for collection'
		);
	}

	if (targetCollectionName && !relevantState[targetCollectionName]) {
		throw new Error(
			'target collection not found in root reducer. please check name for typos or add reducer for collection'
		);
	}

	let mappedProps = {
		emptyState: ownProps.emptyState,
		collectionName: ownProps.collectionName,
		targetCollectionName: ownProps.targetCollectionName,
		updateQueryParams: ownProps.updateQueryParams,
		transformRecords: ownProps.transformRecords,
		EventComponent: ownProps.EventComponent,
		updateFilters: ownProps.updateFilters,
		initialDateRange: ownProps.initialDateRange,
		data: null,
		filters: null,
		sorting: null,
		fetching: null,
	};

	let relevantRecords;
	if (ownProps.useDistinctCollection) {
		relevantRecords = relevantState.distinctRecords;
	} else {
		relevantRecords = relevantState.records;
	}
	if (targetCollectionName) {
		mappedProps.data = getEntitiesById(
			relevantState[ownProps.targetCollectionName].recordsIds,
			relevantRecords
		);
		mappedProps.filters = relevantState[ownProps.targetCollectionName].filters;
		mappedProps.sorting = relevantState[ownProps.targetCollectionName].sorting;
		mappedProps.fetching = relevantState[ownProps.targetCollectionName].fetching;
	} else {
		mappedProps.data = getEntitiesById(relevantState.recordsIds, relevantRecords);
		mappedProps.filters = relevantState.filters;
		mappedProps.sorting = relevantState.sorting;
		mappedProps.fetching = relevantState.fetching;
	}

	return mappedProps;
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchData: (params, targetCollectionName, sorting, filters, addToTargetCollection) =>
		dispatch(
			ownProps.fetchData(params, targetCollectionName, sorting, filters, addToTargetCollection)
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReduxCalendar));
